/**
 * SurveyModal — NPS + feature survey shown to trial users around day 4.
 * Dismissed permanently once submitted or explicitly skipped.
 */
import React, { useState, useEffect } from 'react';
import './SurveyModal.css';

const DEFAULT_QUESTIONS = [
  { id: 'ease', text: 'How easy was it to get started with Oakdesk?', type: 'rating', max: 10 },
  {
    id: 'top_feature',
    text: 'Which feature have you found most valuable so far?',
    type: 'choice',
    options: ['AI Phone Receptionist', 'WhatsApp integration', 'CRM & contacts', 'Bookings', 'Analytics', 'Other'],
  },
  { id: 'improve', text: 'What is one thing we could improve?', type: 'text' },
  { id: 'nps', text: 'How likely are you to recommend Oakdesk to a colleague? (0 = not at all, 10 = definitely)', type: 'rating', max: 10 },
];

function RatingInput({ max, value, onChange }) {
  return (
    <div className="sm-rating">
      {Array.from({ length: max + 1 }, (_, i) => (
        <button
          key={i}
          type="button"
          className={`sm-rating__btn${value === i ? ' sm-rating__btn--active' : ''}`}
          onClick={() => onChange(i)}
        >
          {i}
        </button>
      ))}
    </div>
  );
}

export default function SurveyModal({ user, createdAt, onClose }) {
  const [survey, setSurvey] = useState(null);
  const [answers, setAnswers] = useState({});
  const [step, setStep] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const key = `oakdesk_survey_done_${user?.id}`;
    if (!user || localStorage.getItem(key)) return;

    // Show survey on day 3–5 of trial
    const signupDate = createdAt ? new Date(createdAt) : null;
    if (!signupDate) return;
    const daysSince = Math.floor((new Date() - signupDate) / (1000 * 60 * 60 * 24));
    if (daysSince < 3 || daysSince > 6) return;

    // Fetch active survey questions
    const token = localStorage.getItem('oakdesk_token') || sessionStorage.getItem('oakdesk_token') || '';
    fetch('/api/surveys/active', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        setSurvey(data || { questions: DEFAULT_QUESTIONS, id: 0 });
        setTimeout(() => setVisible(true), 1500);
      })
      .catch(() => {
        setSurvey({ questions: DEFAULT_QUESTIONS, id: 0 });
        setTimeout(() => setVisible(true), 1500);
      });
  }, [user, createdAt]);

  if (!visible || !survey) return null;

  const questions = survey.questions || DEFAULT_QUESTIONS;
  const currentQ = questions[step];
  const isLast = step === questions.length - 1;

  const setAnswer = (val) => setAnswers(prev => ({ ...prev, [currentQ.id]: val }));

  const next = () => {
    if (isLast) submitSurvey();
    else setStep(s => s + 1);
  };

  const submitSurvey = async () => {
    const token = localStorage.getItem('oakdesk_token') || sessionStorage.getItem('oakdesk_token') || '';
    const signupDate = createdAt ? new Date(createdAt) : new Date();
    const trialDay = Math.floor((new Date() - signupDate) / (1000 * 60 * 60 * 24));
    try {
      await fetch('/api/surveys/responses', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          answers,
          nps_score: typeof answers.nps === 'number' ? answers.nps : null,
          trial_day: trialDay,
        }),
      });
    } catch (_) {}
    localStorage.setItem(`oakdesk_survey_done_${user?.id}`, '1');
    setSubmitted(true);
    setTimeout(() => { setVisible(false); onClose?.(); }, 2500);
  };

  const skip = () => {
    localStorage.setItem(`oakdesk_survey_done_${user?.id}`, '1');
    setVisible(false);
    onClose?.();
  };

  return (
    <div className="sm-overlay" onClick={e => { if (e.target === e.currentTarget) skip(); }}>
      <div className="sm-modal">
        {submitted ? (
          <div className="sm-success">
            <div className="sm-success__icon">✓</div>
            <h3>Thank you!</h3>
            <p>Your feedback helps us build a better product for you.</p>
          </div>
        ) : (
          <>
            <div className="sm-header">
              <div className="sm-header__eyebrow">Quick feedback · {step + 1} of {questions.length}</div>
              <button className="sm-skip" onClick={skip} aria-label="Skip survey">Skip</button>
            </div>
            <div className="sm-progress">
              <div className="sm-progress__fill" style={{ width: `${((step + 1) / questions.length) * 100}%` }} />
            </div>
            <div className="sm-body">
              <p className="sm-question">{currentQ.text}</p>
              {currentQ.type === 'rating' && (
                <RatingInput max={currentQ.max || 10} value={answers[currentQ.id] ?? null} onChange={setAnswer} />
              )}
              {currentQ.type === 'choice' && (
                <div className="sm-choices">
                  {currentQ.options.map(opt => (
                    <button
                      key={opt}
                      type="button"
                      className={`sm-choice${answers[currentQ.id] === opt ? ' sm-choice--active' : ''}`}
                      onClick={() => setAnswer(opt)}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              )}
              {currentQ.type === 'text' && (
                <textarea
                  className="sm-textarea"
                  placeholder="Your answer…"
                  rows={3}
                  value={answers[currentQ.id] || ''}
                  onChange={e => setAnswer(e.target.value)}
                />
              )}
            </div>
            <div className="sm-footer">
              {step > 0 && (
                <button className="sm-btn sm-btn--ghost" onClick={() => setStep(s => s - 1)}>← Back</button>
              )}
              <button
                className="sm-btn sm-btn--primary"
                onClick={next}
                disabled={answers[currentQ.id] === undefined || answers[currentQ.id] === ''}
              >
                {isLast ? 'Submit feedback' : 'Next →'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
